import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconChevronDown, IconClockOut, IconClose, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

type Tab = 'timesheet' | 'clock';

interface Filters {
  search: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', dateFrom: '', dateTo: '' };

interface TimesheetDayEntry {
  id: number;
  date: string;
  activity: string;
  total_hours: number;
  start_time: string | null;
  end_time: string | null;
  status: 'pending' | 'approved' | 'rejected';
  decided_by_name: string | null;
  decision_comment: string | null;
  is_pending: boolean;
  approval_levels_required: number;
  current_approval_level: number;
  pending_with_name: string | null;
  approvals: ApprovalHistoryEntry[];
}

interface ApprovalHistoryEntry {
  level: number;
  approver_name: string | null;
  decision: string;
  comment: string | null;
  decided_at: string;
}

interface TimesheetRow {
  user_id: number;
  user_name: string;
  employee_code: string | null;
  period_start: string;
  period_end: string;
  total_hours: number;
  entry_count: number;
  entries: TimesheetDayEntry[];
}

type WorkLocation = 'office' | 'wfh' | 'field';

interface AttendanceEntryRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  clock_in_at: string;
  clock_out_at: string | null;
  duration_minutes: number | null;
  notes: string | null;
  work_location: WorkLocation;
  is_open: boolean;
}

interface ImportRowError {
  row: number;
  email: string | null;
  errors: string[];
}

const WORK_LOCATION_LABELS: Record<WorkLocation, string> = { office: 'Office', wfh: 'WFH', field: 'Field' };

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '—';
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function formatHours(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(2)}h`;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

// datetime-local inputs need "YYYY-MM-DDTHH:mm" in local time, not the ISO
// UTC string the API returns.
function toDateTimeLocal(value: string): string {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ISO 8601 week helpers for the Timesheet "Period" picker (<input
// type="week">, value shape "YYYY-Www").
function isoWeekString(d: Date): string {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function mondayOfIsoWeek(weekValue: string): Date {
  const [yearStr, weekStr] = weekValue.split('-W');
  const year = Number(yearStr);
  const week = Number(weekStr);
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dow = simple.getUTCDay();
  const diff = dow <= 4 ? 1 - dow : 8 - dow;
  const monday = new Date(simple);
  monday.setUTCDate(simple.getUTCDate() + diff);
  return monday;
}

// Local calendar date as "YYYY-MM-DD" — deliberately not toISOString(),
// which converts to UTC and can land on the wrong side of midnight
// depending on the browser's timezone offset.
function localDateString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function weekDays(weekValue: string): { date: string; label: string }[] {
  const monday = mondayOfIsoWeek(weekValue);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    return {
      date: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
    };
  });
}

const emptyTimesheetForm = { period: '', day: '', activity: '', total_hours: '', start_time: '', end_time: '' };

const STATUS_BADGE: Record<TimesheetDayEntry['status'], string> = {
  pending: 'badge-scheduled',
  approved: 'badge-active',
  rejected: 'badge-cancelled',
};

export function Attendance() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();

  const [tab, setTab] = useState<Tab>('timesheet');
  const [timesheetFilters, setTimesheetFilters] = useState<Filters>(emptyFilters);
  const [timesheetStatus, setTimesheetStatus] = useState('');
  const [clockFilters, setClockFilters] = useState<Filters>(emptyFilters);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [clockInLocation, setClockInLocation] = useState<WorkLocation>('office');

  const [editingEntry, setEditingEntry] = useState<AttendanceEntryRow | null>(null);
  const [editForm, setEditForm] = useState({ clock_in_at: '', clock_out_at: '', notes: '', work_location: 'office' as WorkLocation });

  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<ImportRowError[]>([]);

  const [timesheetAddOpen, setTimesheetAddOpen] = useState(false);
  const [timesheetForm, setTimesheetForm] = useState(emptyTimesheetForm);
  const [editingTimesheetEntry, setEditingTimesheetEntry] = useState<{ id: number; user_name: string } | null>(null);
  const [timesheetEditForm, setTimesheetEditForm] = useState({
    date: '',
    activity: '',
    total_hours: '',
    start_time: '',
    end_time: '',
  });

  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canUpdate = can('hr.update');
  const canArchive = can('hr.archive');
  const canApprove = can('hr.approve');

  const filters = tab === 'timesheet' ? timesheetFilters : clockFilters;
  const setFilters = tab === 'timesheet' ? setTimesheetFilters : setClockFilters;

  const timesheetQuery = useMemo(
    () => ({
      search: timesheetFilters.search || undefined,
      status: timesheetStatus || undefined,
      date_from: timesheetFilters.dateFrom || undefined,
      date_to: timesheetFilters.dateTo || undefined,
    }),
    [timesheetFilters, timesheetStatus]
  );
  const clockQuery = useMemo(
    () => ({
      search: clockFilters.search || undefined,
      date_from: clockFilters.dateFrom || undefined,
      date_to: clockFilters.dateTo || undefined,
    }),
    [clockFilters]
  );

  const timesheets = usePaginatedList<TimesheetRow>('/timesheets', timesheetQuery);
  const entries = usePaginatedList<AttendanceEntryRow>('/attendance-entries', clockQuery);
  const active = tab === 'timesheet' ? timesheets : entries;

  const toggleExpanded = (key: string) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleClock = async () => {
    try {
      await api.post('/attendance-entries/toggle', { work_location: clockInLocation });
      entries.reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Clock action failed.');
    }
  };

  const clockOut = async (entry: AttendanceEntryRow) => {
    try {
      await api.post(`/attendance-entries/${entry.id}/clock-out`);
      entries.reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Clock out failed.');
    }
  };

  const openEdit = (entry: AttendanceEntryRow) => {
    setEditingEntry(entry);
    setEditForm({
      clock_in_at: toDateTimeLocal(entry.clock_in_at),
      clock_out_at: entry.clock_out_at ? toDateTimeLocal(entry.clock_out_at) : '',
      notes: entry.notes ?? '',
      work_location: entry.work_location,
    });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingEntry) return;
    try {
      await api.put(`/attendance-entries/${editingEntry.id}`, {
        clock_in_at: editForm.clock_in_at,
        clock_out_at: editForm.clock_out_at || null,
        notes: editForm.notes || null,
        work_location: editForm.work_location,
      });
      setEditingEntry(null);
      entries.reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const onImportFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] ?? null);
    setImportErrors([]);
  };

  const submitImport = async () => {
    if (!importFile) return;
    const form = new FormData();
    form.append('file', importFile);
    try {
      const res = await api.post<{ imported: number }>('/attendance-entries/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSuccess(`Imported ${res.data.imported} entr${res.data.imported === 1 ? 'y' : 'ies'}.`);
      setImporting(false);
      setImportFile(null);
      setImportErrors([]);
      entries.reload();
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.errors)) {
        setImportErrors(err.errors as unknown as ImportRowError[]);
      } else {
        showError(err instanceof ApiError ? err.message : 'Import failed.');
      }
    }
  };

  const remove = async (entry: AttendanceEntryRow) => {
    if (!(await confirmAction(`Delete this attendance entry for ${entry.user_name}?`))) return;
    try {
      await api.delete(`/attendance-entries/${entry.id}`);
      entries.reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const openTimesheetAdd = () => {
    const today = new Date();
    const period = isoWeekString(today);
    const days = weekDays(period);
    const todayIso = localDateString(today);
    setTimesheetForm({
      period,
      day: days.some((d) => d.date === todayIso) ? todayIso : days[0].date,
      activity: '',
      total_hours: '',
      start_time: '',
      end_time: '',
    });
    setErrors({});
    setTimesheetAddOpen(true);
  };

  const onPeriodChange = (period: string) => {
    const days = weekDays(period);
    setTimesheetForm((f) => ({ ...f, period, day: days[0].date }));
  };

  const submitTimesheetAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/timesheet-entries', {
        date: timesheetForm.day,
        activity: timesheetForm.activity,
        total_hours: Number(timesheetForm.total_hours),
        start_time: timesheetForm.start_time || null,
        end_time: timesheetForm.end_time || null,
      });
      setTimesheetAddOpen(false);
      timesheets.reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openTimesheetEdit = (userName: string, entry: TimesheetDayEntry) => {
    setEditingTimesheetEntry({ id: entry.id, user_name: userName });
    setTimesheetEditForm({
      date: entry.date,
      activity: entry.activity,
      total_hours: String(entry.total_hours),
      start_time: entry.start_time ?? '',
      end_time: entry.end_time ?? '',
    });
    setErrors({});
  };

  const submitTimesheetEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingTimesheetEntry) return;
    try {
      await api.put(`/timesheet-entries/${editingTimesheetEntry.id}`, {
        date: timesheetEditForm.date,
        activity: timesheetEditForm.activity,
        total_hours: Number(timesheetEditForm.total_hours),
        start_time: timesheetEditForm.start_time || null,
        end_time: timesheetEditForm.end_time || null,
      });
      setEditingTimesheetEntry(null);
      timesheets.reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const decideTimesheet = async (userName: string, entry: TimesheetDayEntry, decision: 'approved' | 'rejected') => {
    const verb = decision === 'approved' ? 'Approve' : 'Reject';
    const confirmed = await confirmAction(`${verb} ${userName}'s ${formatDate(entry.date)} timesheet entry?`, {
      confirmLabel: verb,
    });
    if (!confirmed) return;
    try {
      await api.post(`/timesheet-entries/${entry.id}/decide`, { decision });
      timesheets.reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : `${verb} failed.`);
    }
  };

  const removeTimesheetEntry = async (userName: string, entry: TimesheetDayEntry) => {
    if (!(await confirmAction(`Delete this timesheet entry for ${userName}?`))) return;
    try {
      await api.delete(`/timesheet-entries/${entry.id}`);
      timesheets.reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Attendance</h1>
        <div className="page-header-actions">
          {tab === 'clock' && (
            <select value={clockInLocation} onChange={(e) => setClockInLocation(e.target.value as WorkLocation)}>
              {(Object.entries(WORK_LOCATION_LABELS) as [WorkLocation, string][]).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}
          {tab === 'clock' && canUpdate && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setImportFile(null);
                setImportErrors([]);
                setImporting(true);
              }}
            >
              Import CSV
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={tab === 'clock' ? toggleClock : openTimesheetAdd}>
            + Add
          </button>
        </div>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          <button type="button" className={tab === 'timesheet' ? 'tab active' : 'tab'} onClick={() => setTab('timesheet')}>
            Timesheet
          </button>
          <button type="button" className={tab === 'clock' ? 'tab active' : 'tab'} onClick={() => setTab('clock')}>
            Clock In-Out
          </button>
        </div>
      </div>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        {tab === 'timesheet' && (
          <select value={timesheetStatus} onChange={(e) => setTimesheetStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
        <label className="checkbox-inline">
          Date from
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          {tab === 'timesheet' ? (
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Employee Code</th>
                  <th>Name</th>
                  <th>Period</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {timesheets.loading ? (
                  <tr>
                    <td colSpan={7} className="muted center">
                      Loading…
                    </td>
                  </tr>
                ) : timesheets.error ? (
                  <tr>
                    <td colSpan={7} className="error center">
                      {timesheets.error}
                    </td>
                  </tr>
                ) : timesheets.data && timesheets.data.data.length > 0 ? (
                  timesheets.data.data.flatMap((row) => {
                    const key = `${row.user_id}|${row.period_start}`;
                    const expanded = expandedWeeks.has(key);
                    const weekRow = (
                      <tr key={key}>
                        <td>
                          <button
                            type="button"
                            className="btn icon-btn btn-ghost"
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            onClick={() => toggleExpanded(key)}
                          >
                            <IconChevronDown
                              className="icon-btn-svg"
                              style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                            />
                          </button>
                        </td>
                        <td className="muted">{row.employee_code ?? '—'}</td>
                        <td>
                          <strong>{row.user_name}</strong>
                        </td>
                        <td>{formatPeriod(row.period_start, row.period_end)}</td>
                        <td>
                          {formatHours(row.total_hours)}{' '}
                          <small className="muted">
                            · {row.entry_count} entr{row.entry_count === 1 ? 'y' : 'ies'}
                          </small>
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    );

                    if (!expanded) return [weekRow];

                    const dayRows = row.entries.flatMap((entry) => {
                      const entryRow = (
                        <tr key={`entry-${entry.id}`}>
                          <td></td>
                          <td></td>
                          <td className="muted">{formatDate(entry.date)}</td>
                          <td>{labelFor(lists, 'timesheet_activity', entry.activity)}</td>
                          <td>
                            {formatHours(entry.total_hours)}
                            {entry.start_time && entry.end_time && (
                              <>
                                <br />
                                <small className="muted">
                                  {entry.start_time}–{entry.end_time}
                                </small>
                              </>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${STATUS_BADGE[entry.status]}`}>{entry.status}</span>
                            {entry.is_pending && (
                              <>
                                <br />
                                <small className="muted">
                                  Pending with: {entry.pending_with_name ?? 'no manager set'}
                                  {entry.approval_levels_required > 1 &&
                                    ` (level ${entry.current_approval_level} of ${entry.approval_levels_required})`}
                                </small>
                              </>
                            )}
                          </td>
                          <td className="right">
                            {canApprove && entry.is_pending && (
                              <>
                                <IconButton
                                  icon={IconCheck}
                                  label="Approve"
                                  onClick={() => decideTimesheet(row.user_name, entry, 'approved')}
                                />
                                <IconButton
                                  icon={IconClose}
                                  label="Reject"
                                  variant="danger-ghost"
                                  onClick={() => decideTimesheet(row.user_name, entry, 'rejected')}
                                />
                              </>
                            )}
                            {canUpdate && (
                              <IconButton icon={IconEdit} label="Edit" onClick={() => openTimesheetEdit(row.user_name, entry)} />
                            )}
                            {canArchive && (
                              <IconButton
                                icon={IconTrash}
                                label="Delete"
                                variant="danger-ghost"
                                onClick={() => removeTimesheetEntry(row.user_name, entry)}
                              />
                            )}
                          </td>
                        </tr>
                      );

                      const approvalRows = entry.approvals.map((a, i) => (
                        <tr key={`approval-${entry.id}-${i}`}>
                          <td></td>
                          <td></td>
                          <td colSpan={2} className="muted">
                            Level {a.level} — {a.approver_name ?? 'Unknown'}
                            {a.comment && <>: {a.comment}</>}
                          </td>
                          <td>
                            <span className={`badge ${a.decision === 'approved' ? 'badge-active' : 'badge-cancelled'}`}>
                              {a.decision}
                            </span>
                          </td>
                          <td colSpan={2} className="muted">
                            {new Date(a.decided_at).toLocaleString('en-GB')}
                          </td>
                        </tr>
                      ));

                      return [entryRow, ...approvalRows];
                    });

                    return [weekRow, ...dayRows];
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="muted center">
                      No timesheets yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Duration</th>
                  <th>Location</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.loading ? (
                  <tr>
                    <td colSpan={6} className="muted center">
                      Loading…
                    </td>
                  </tr>
                ) : entries.error ? (
                  <tr>
                    <td colSpan={6} className="error center">
                      {entries.error}
                    </td>
                  </tr>
                ) : entries.data && entries.data.data.length > 0 ? (
                  entries.data.data.map((entry) => (
                    <tr key={entry.id}>
                      <td className="muted">{entry.employee_code ?? '—'}</td>
                      <td>
                        <strong>{entry.user_name}</strong>
                      </td>
                      <td>
                        {formatDateTime(entry.clock_in_at)}
                        {entry.is_open ? (
                          <>
                            {' '}
                            <span className="badge badge-important">open</span>
                          </>
                        ) : (
                          <>
                            {' '}
                            <span className="muted">– {formatDateTime(entry.clock_out_at as string)}</span>
                          </>
                        )}
                      </td>
                      <td>{formatDuration(entry.duration_minutes)}</td>
                      <td>
                        <span className={`badge ${entry.work_location === 'office' ? 'badge-neutral' : 'badge-scheduled'}`}>
                          {WORK_LOCATION_LABELS[entry.work_location]}
                        </span>
                      </td>
                      <td className="right">
                        {canUpdate && entry.is_open && (
                          <IconButton icon={IconClockOut} label="Clock out" onClick={() => clockOut(entry)} />
                        )}
                        {canUpdate && <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(entry)} />}
                        {canArchive && (
                          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(entry)} />
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="muted center">
                      No entries yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <Pagination meta={active.data} onPage={active.setPage} />
      </div>

      {editingEntry && (
        <Modal title={`Edit entry for ${editingEntry.user_name}`} onClose={() => setEditingEntry(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Clock in *
              <input
                type="datetime-local"
                value={editForm.clock_in_at}
                onChange={(e) => setEditForm({ ...editForm, clock_in_at: e.target.value })}
                required
              />
              {errors.clock_in_at && <small className="error">{errors.clock_in_at[0]}</small>}
            </label>
            <label>
              Clock out
              <input
                type="datetime-local"
                value={editForm.clock_out_at}
                onChange={(e) => setEditForm({ ...editForm, clock_out_at: e.target.value })}
              />
              {errors.clock_out_at && <small className="error">{errors.clock_out_at[0]}</small>}
            </label>
            <label>
              Work location
              <select
                value={editForm.work_location}
                onChange={(e) => setEditForm({ ...editForm, work_location: e.target.value as WorkLocation })}
              >
                {(Object.entries(WORK_LOCATION_LABELS) as [WorkLocation, string][]).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="span-2">
              Notes
              <textarea
                rows={3}
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingEntry(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {importing && (
        <Modal title="Import attendance from CSV" onClose={() => setImporting(false)}>
          <p className="muted">
            One row per shift — the export from any biometric/swipe/door-access device. Columns:{' '}
            <code>email, clock_in</code> (required), <code>clock_out, work_location, notes</code> (optional).
            <code>work_location</code> must be office, wfh, or field, and defaults to office. If any row fails
            validation, nothing is imported.
          </p>
          <input type="file" accept=".csv,text/csv" onChange={onImportFileSelected} />
          {importErrors.length > 0 && (
            <div className="alert alert-error" style={{ marginTop: 12 }}>
              <strong>{importErrors.length} row(s) failed validation — nothing was imported:</strong>
              <ul>
                {importErrors.map((e) => (
                  <li key={e.row}>
                    Row {e.row} ({e.email ?? 'no email'}): {e.errors.join(' ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setImporting(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={!importFile} onClick={submitImport}>
              Import
            </button>
          </div>
        </Modal>
      )}

      {timesheetAddOpen && (
        <Modal title="Add timesheet entry" onClose={() => setTimesheetAddOpen(false)}>
          <form onSubmit={submitTimesheetAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Period *
              <input type="week" value={timesheetForm.period} onChange={(e) => onPeriodChange(e.target.value)} required />
            </label>
            <label className="span-2">
              Day *
              <select
                value={timesheetForm.day}
                onChange={(e) => setTimesheetForm({ ...timesheetForm, day: e.target.value })}
                required
              >
                {weekDays(timesheetForm.period).map((d) => (
                  <option key={d.date} value={d.date}>
                    {d.label}
                  </option>
                ))}
              </select>
              {errors.date && <small className="error">{errors.date[0]}</small>}
            </label>
            <label className="span-2">
              Activity *
              <select
                value={timesheetForm.activity}
                onChange={(e) => setTimesheetForm({ ...timesheetForm, activity: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {listOptions(lists, 'timesheet_activity').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.activity && <small className="error">{errors.activity[0]}</small>}
            </label>
            <label>
              Total hours *
              <input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={timesheetForm.total_hours}
                onChange={(e) => setTimesheetForm({ ...timesheetForm, total_hours: e.target.value })}
                required
              />
              {errors.total_hours && <small className="error">{errors.total_hours[0]}</small>}
            </label>
            <div />
            <label>
              Start time
              <input
                type="time"
                value={timesheetForm.start_time}
                onChange={(e) => setTimesheetForm({ ...timesheetForm, start_time: e.target.value })}
              />
            </label>
            <label>
              End time
              <input
                type="time"
                value={timesheetForm.end_time}
                onChange={(e) => setTimesheetForm({ ...timesheetForm, end_time: e.target.value })}
              />
              {errors.end_time && <small className="error">{errors.end_time[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setTimesheetAddOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!timesheetForm.activity || !timesheetForm.total_hours}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingTimesheetEntry && (
        <Modal
          title={`Edit timesheet entry for ${editingTimesheetEntry.user_name}`}
          onClose={() => setEditingTimesheetEntry(null)}
        >
          <form onSubmit={submitTimesheetEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Date *
              <input
                type="date"
                value={timesheetEditForm.date}
                onChange={(e) => setTimesheetEditForm({ ...timesheetEditForm, date: e.target.value })}
                required
              />
              {errors.date && <small className="error">{errors.date[0]}</small>}
            </label>
            <label className="span-2">
              Activity *
              <select
                value={timesheetEditForm.activity}
                onChange={(e) => setTimesheetEditForm({ ...timesheetEditForm, activity: e.target.value })}
                required
              >
                {listOptions(lists, 'timesheet_activity').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.activity && <small className="error">{errors.activity[0]}</small>}
            </label>
            <label>
              Total hours *
              <input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={timesheetEditForm.total_hours}
                onChange={(e) => setTimesheetEditForm({ ...timesheetEditForm, total_hours: e.target.value })}
                required
              />
              {errors.total_hours && <small className="error">{errors.total_hours[0]}</small>}
            </label>
            <div />
            <label>
              Start time
              <input
                type="time"
                value={timesheetEditForm.start_time}
                onChange={(e) => setTimesheetEditForm({ ...timesheetEditForm, start_time: e.target.value })}
              />
            </label>
            <label>
              End time
              <input
                type="time"
                value={timesheetEditForm.end_time}
                onChange={(e) => setTimesheetEditForm({ ...timesheetEditForm, end_time: e.target.value })}
              />
              {errors.end_time && <small className="error">{errors.end_time[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingTimesheetEntry(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
